// Monstres et boss (dossier §5). Valeurs initiales du dossier, à mesurer.
import type { ZoneLevel } from './shapes';

export type Archetype = 'bastion' | 'faucheur' | 'sentinelle';
export type BossKind = Archetype | 'chimere';

export const ARCH_INDEX: Record<BossKind, number> = { bastion: 0, faucheur: 1, sentinelle: 2, chimere: 3 };
export const ARCH_BY_INDEX: BossKind[] = ['bastion', 'faucheur', 'sentinelle', 'chimere'];

export interface MonsterProfile {
  hp: number;
  damage: number;
  /** Intervalle entre deux attaques (s). */
  interval: number;
  speed: number;
  xp: number;
  radius: number;
  ranged: boolean;
}

/** Zone 1 (vert), table du dossier. */
export const MONSTER_BASE: Record<Archetype, MonsterProfile> = {
  bastion: { hp: 600, damage: 8, interval: 1, speed: 80, xp: 160, radius: 38, ranged: false },
  faucheur: { hp: 180, damage: 24, interval: 1.2, speed: 130, xp: 160, radius: 26, ranged: false },
  sentinelle: { hp: 350, damage: 14, interval: 1.5, speed: 75, xp: 160, radius: 30, ranged: true },
};

/** Multiplicateurs par zone : PV, dégâts, XP. */
export const ZONE_SCALING: Record<1 | 2 | 3, { hp: number; damage: number; xp: number }> = {
  1: { hp: 1, damage: 1, xp: 1 },
  2: { hp: 2.5, damage: 2, xp: 2.5 },
  3: { hp: 6, damage: 3.5, xp: 6 },
};

export function monsterProfile(arch: Archetype, zone: 1 | 2 | 3): MonsterProfile {
  const b = MONSTER_BASE[arch];
  const z = ZONE_SCALING[zone];
  return { ...b, hp: b.hp * z.hp, damage: b.damage * z.damage, xp: b.xp * z.xp, radius: b.radius * (1 + (zone - 1) * 0.12) };
}

/** Monstres par million d'unités² de région, selon la zone (budgets bornés). */
export const MONSTER_DENSITY: Record<1 | 2 | 3, number> = { 1: 1.2, 2: 1.0, 3: 0.8 };
export const MONSTER_WEIGHTS: Record<Archetype, number> = { bastion: 35, faucheur: 35, sentinelle: 30 };
export const MONSTER_RESPAWN_S = 12;
export const MONSTER_TELEGRAPH_S = { melee: 0.35, ranged: 0.45 };
export const MONSTER_MAX_PURSUERS = 3;
export const MONSTER_PROJECTILE = { speed: 430, life: 1.7, radius: 9 };

export function aggroRadius(zone: ZoneLevel): number {
  return 420 + zone * 40;
}

// ─── Boss ────────────────────────────────────────────────────────────────

export interface BossProfile {
  hp: number;
  contactDamage: number;
  projectileDamage: number;
  reserve: number;
  radius: number;
  speed: number;
}

/**
 * PV calibrés avec scripts/boss-bench.ts (choix de Léni, 3 novembre 2026 : 4 à 6 tanks de haut niveau doivent
 * pouvoir battre un boss). Dossier : 80 000 / 45 000 / 60 000 / 140 000. Dégâts et réserves du dossier inchangés.
 */
export const BOSS_PROFILES: Record<BossKind, BossProfile> = {
  bastion: { hp: 40000, contactDamage: 30, projectileDamage: 30, reserve: 30000, radius: 120, speed: 55 },
  faucheur: { hp: 36000, contactDamage: 80, projectileDamage: 0, reserve: 30000, radius: 90, speed: 95 },
  sentinelle: { hp: 30000, contactDamage: 20, projectileDamage: 45, reserve: 30000, radius: 100, speed: 50 },
  chimere: { hp: 56000, contactDamage: 80, projectileDamage: 45, reserve: 90000, radius: 135, speed: 70 },
};

export const BOSS_WEIGHTS: Record<BossKind, number> = { bastion: 30, faucheur: 30, sentinelle: 30, chimere: 10 };
export const BOSS_FIRST_DELAY_S = 300;
export const BOSS_RESPAWN_DELAY_S = 300;
export const BOSS_MAX_FIGHT_S = 300;
/** PV fixés au spawn selon la population active, facteur plafonné (pas de hausse quand des spectateurs entrent). */
export function bossPopulationFactor(activeParticipants: number): number {
  return Math.min(1.2, Math.max(0.5, 0.4 + 0.025 * activeParticipants));
}

/**
 * Niveau 250 (choix de Léni, 5 novembre 2026) : PV du boss multipliés selon le niveau des tanks de l'arène à son
 * apparition (moyenne des 4 plus hauts niveaux), pour garder des combats de 90 à 180 s à tout niveau. ×1 jusqu'au
 * niveau 60, puis ≈ ×3,4 au niveau 100, ×7,2 au niveau 175, ×9,7 au niveau 250 : calé sur scripts/boss-bench.ts
 * (temps de combat de 4 tanks mesuré à ces niveaux, rapporté à celui du niveau 60). Fixé au spawn, comme la population.
 */
export function bossLevelFactor(levels: number[]): number {
  const top = [...levels].sort((a, b) => b - a).slice(0, 4);
  if (top.length === 0) return 1;
  const over = Math.max(0, Math.min(190, top.reduce((s, l) => s + l, 0) / top.length - 60));
  return 1 + 0.065 * over - 0.0001 * over * over;
}
export const ROTATION_INTERVAL_S = 900;
export const ROTATION_WARNINGS_S = [60, 30, 10];
